import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import ClickableJapaneseText from '../japanese/ClickableJapaneseText';

interface Comment {
  title: string;
  body: string;
}

interface Thread {
  title: string;
  post: string;
  comments: Comment[];
}

const stripWhitespace = (s: string) => s.replace(/\s+/g, '');

const parseThreadHTML = (html: string): Thread => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = doc.querySelector('h1')?.textContent?.trim() ?? '';
  const p = doc.querySelector('.post p');
  if (!p) return { title, post: '', comments: [] };

  const segments: string[] = [];
  let current = '';
  let pendingBR = false;

  for (const node of Array.from(p.childNodes)) {
    if (node.nodeName === 'BR') {
      if (pendingBR) {
        const trimmed = current.trim();
        if (trimmed) segments.push(trimmed);
        current = '';
        pendingBR = false;
      } else {
        current += '\n';
        pendingBR = true;
      }
    } else {
      const text = node.textContent ?? '';
      if (text.trim()) pendingBR = false;
      current += text;
    }
  }
  const trimmed = current.trim();
  if (trimmed) segments.push(trimmed);

  if (segments.length === 0) return { title, post: '', comments: [] };

  const [postSegment, ...commentSegments] = segments;
  const post = stripWhitespace(postSegment);

  const comments = commentSegments.map(seg => {
    const firstNewline = seg.indexOf('\n');
    if (firstNewline === -1) return { title: stripWhitespace(seg), body: '' };
    return {
      title: stripWhitespace(seg.slice(0, firstNewline)),
      body: stripWhitespace(seg.slice(firstNewline + 1)),
    };
  });

  return { title, post, comments };
};

export default function KomachiThread() {
  const { id } = useParams<{ id: string }>();
  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetch(`/rss/komachi/${id}.html`)
      .then(r => r.text())
      .then(html => setThread(parseThreadHTML(html)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Helmet>
        <title>{thread?.title ?? 'Komachi Reader'}</title>
      </Helmet>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/komachi" className="text-zinc-600 hover:text-zinc-400 text-sm transition-colors duration-150">← back</Link>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-5 h-5 border-2 border-t-zinc-400 border-zinc-700 rounded-full animate-spin mt-8" />
          </div>
        )}

        {error && (
          <p className="text-zinc-600 text-sm mt-8">failed to load thread</p>
        )}

        {thread && (
          <>
            <h1 className="text-zinc-100 font-medium mt-8 mb-6 leading-relaxed text-2xl md:text-3xl">
              <ClickableJapaneseText text={thread.title} />
            </h1>

            <div className="text-zinc-300 leading-loose mb-8 text-xl md:text-2xl">
              <ClickableJapaneseText text={thread.post} />
            </div>

            {thread.comments.length > 0 && (
              <>
                <div className="border-t border-zinc-800 mb-6" />
                <div className="flex flex-col gap-8">
                  {thread.comments.map((comment, i) => (
                    <div key={i}>
                      {comment.title && (
                        <p className="text-zinc-500 font-medium mb-2 text-base md:text-lg">
                          <ClickableJapaneseText text={comment.title} />
                        </p>
                      )}
                      {comment.body && (
                        <p className="text-zinc-300 leading-loose text-xl md:text-2xl">
                          <ClickableJapaneseText text={comment.body} />
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
